<template>
	<section
		:class="{
			'confirmation-page__loading-background': (finishRequestPending || shoppingCartRequestPending) && !tokenExpired && !unknownError,
		}"
		class="confirmation-page"
	>
		<div v-if="!shoppingCartRequestPending && !unknownError && !tokenExpired && shoppingCartResponse">
			<CheckoutThankYou
				:shopping-cart="shoppingCartResponse"
				:client="client"
			/>
		</div>
		<div
			v-else-if="!tokenExpired && !unknownError"
			class="confirmation-page__loading"
		>
			<p v-if="finishRequestPending">Ihre Buchung ist in Bearbeitung, bitte Fenster nicht schließen.</p>
			<p v-else>Ihre Buchung wird abgefragt. Bitte Fenster nicht schließen.</p>
			<Loading class="confirmation-page__loading-spinner" />
		</div>
		<div
			v-if="!shoppingCartRequestPending"
			class="confirmation-page__error"
		>
			<div v-if="tokenExpired">
				<h1>Bestätigungsseite abgelaufen</h1>
				<p>
					Lieber Kunde, die Details zu Ihrer Buchung können nicht mehr eingesehen werden. Die Buchungsbestätigung wurde Ihnen
					bereits an Ihre Email gesendet. Für weitere Fragen steht Ihnen unser Servicecenter unter
					<a :href="phoneLink">{{ phoneNumber }}</a> zur Verfügung.
				</p>
				<<<<<<< HEAD
				<p><a href="/suche">Zurück zur Suche</a></p>
				======= >>>>>>> 0af4ecf20f (refs #13068 remove link)
			</div>
			<div v-if="unknownError">
				<h1>Es ist ein Fehler aufgetreten!</h1>
				<p>
					Lieber Kunde, bei Ihrer Buchung ist ein Fehler aufgetreten. Bitte prüfen Sie ob Sie eine Auftragsbestätigung per E-Mail
					erhalten haben, dies kann bis zu 15 Minuten dauern. Sollten Sie keine E-Mail erhalten haben, kontaktieren Sie uns bitte
					unter <a :href="phoneLink">{{ phoneNumber }}</a
					>.
				</p>
				<<<<<<< HEAD
				<p><a href="/suche">Zurück zur Suche</a></p>
				======= >>>>>>> 0af4ecf20f (refs #13068 remove link)
			</div>
		</div>
	</section>
</template>
<script lang="ts" setup>
import type { Ref } from 'vue';
import { onMounted, computed, ref } from 'vue';
import type { ShoppingCart } from '@/interfaces/checkout/shopping-cart';
import { getParam } from '@services/url';
import * as finishService from '@services/checkout/finishService';
import * as shoppingCartService from '@services/checkout/shoppingCartService';
import axios from 'axios';
import Loading from '@lmt-rpb/Loading/Loading.vue';
import CheckoutThankYou from '@lmt-rpb/CheckoutThankYou/CheckoutThankYou.vue';
import { useStore } from '@/components/common/store';
import { scrollIt } from '@utils/utils';
import { determineClient } from '@utils/environmentUtils';
import * as checkoutStorageService from '@services/localStorage/checkoutStorageService';
import { useGoogleAnalytics } from 'src/composables/useGoogleAnalytics';

interface RequestData {
	Token: string;

	PaymentStatus: 'OK' | 'ERROR';

	BookingTest?: boolean;
}

const store = useStore();

const client = determineClient(window.location.href);

const { handleConfirmationPageAnalytics } = useGoogleAnalytics();

const shoppingCartResponse: Ref<ShoppingCart | undefined> = ref();

const shoppingCartRequestPending = ref(true);

const finishRequestPending = ref(false);

const tokenExpired = ref(false);

const unknownError = ref(false);

const phoneNumber = computed((): string => store.state.config.phone);

const phoneLink = computed((): string => 'tel:' + store.state.config.phoneLink);

const isBookingTest = ref(getParam('bookingtest') === '1');

onMounted(() => {
	init();
});

const preventTabClosingHandler = (e: BeforeUnloadEvent) => {
	e.preventDefault();
	e.returnValue = true;
};

async function init() {
	scrollIt(document.body);
	document.body.style.pointerEvents = 'none';
	document.body.style.overflow = 'hidden';
	try {
		await setShoppingCart();
	} catch (error) {
		if (error instanceof Error && error.message === 'Expired Token') {
			tokenExpired.value = true;
		} else if (axios.isAxiosError(error) && !axios.isCancel(error)) {
			unknownError.value = true;
		} else {
			unknownError.value = true;
		}
		console.error('Shopping Cart error:', {
			'Error Shopping Cart': JSON.stringify(error),
			'Shopping Cart Response': JSON.stringify(shoppingCartResponse.value),
			'Shopping Cart Token': getParam('token'),
		});
	} finally {
		shoppingCartRequestPending.value = false;
		document.body.style.pointerEvents = 'auto';
		document.body.style.overflow = 'auto';
	}
}

async function setShoppingCart() {
	const token = getParam('token');
	const status = getParam('status') === 'error' ? 'ERROR' : 'OK';
	const requestData = getRequestData(token, status);
	shoppingCartResponse.value = await shoppingCartService.get(requestData.Token, false);
	await finishPurchase(shoppingCartResponse.value!.ShoppingCartID, requestData);
}

async function finishPurchase(id: string, requestData: RequestData) {
	const paymentDoneEntry = checkoutStorageService.getById(id);
	if (paymentDoneEntry?.status === 'payment' || paymentDoneEntry?.status === 'payed') {
		finishRequestPending.value = true;
		window.addEventListener('beforeunload', preventTabClosingHandler);
		checkoutStorageService.updateStatusById(shoppingCartResponse.value!.ShoppingCartID, 'finished');
		shoppingCartResponse.value = await finishService.get(requestData);
		window.removeEventListener('beforeunload', preventTabClosingHandler);
		finishRequestPending.value = false;
		handleConfirmationPageAnalytics(shoppingCartResponse.value, id);
	}
}

function getRequestData(token: string | null, status: 'OK' | 'ERROR') {
	if (!token || token.length < 10) {
		// eg token=error
		throw new Error('Token Invalid');
	}
	const requestData: RequestData = {
		Token: token,
		PaymentStatus: status,
	};
	if (isBookingTest.value) {
		requestData.BookingTest = isBookingTest.value;
	}
	return requestData;
}
</script>

<style lang="scss" scoped>
.confirmation-page {
	margin: 0 auto;
	min-height: 100vh;
	padding-top: 2rem;

	&__loading-background {
		background-color: $color-filter-bg;
		pointer-events: none;
	}

	p {
		font-size: $font-small-1;
		font-style: italic;
		font-weight: $font-weight-semibold;
	}

	@media print {
		min-height: unset;
		padding-top: 0;
	}

	&__loading {
		padding: 0 1rem;
		margin: 4rem auto;
		text-align: center;
	}

	&__loading-spinner {
		margin: auto;
	}

	&__error {
		max-width: 67.5rem;
		margin: auto;
		font-size: $font-medium-3;

		p {
			margin-bottom: 2rem;
		}
	}
}
</style>
